import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

export const SUBTYPES = { CAMBIO_TARGA: "Cambio targa", AREA_C: "Attivazione Area C", NUOVO: "Nuovo Telepass", DISATTIVAZIONE: "Disattivazione / guasto" };
const NONE = "__none";
const blank = { type: "MATERIALE", subtype: "", description: "", quantity: "", telepass_id: NONE, vehicle_id: NONE, notes: "", station_id: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const RequestDialog = ({ item, stations, telepass, vehicles, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) setF({ ...blank, station_id: stations[0]?.id || "", ...item, subtype: item.subtype || "", telepass_id: item.telepass_id || NONE, vehicle_id: item.vehicle_id || NONE });
  }, [item, stations]);
  if (!item) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const station = isAdmin ? f.station_id : stations[0]?.id;
  const tpOpts = telepass.filter((t) => t.station_id === station);
  const vOpts = vehicles.filter((v) => v.station_id === station);
  const isTp = f.type === "TELEPASS";
  const needTp = isTp && f.subtype && f.subtype !== "NUOVO";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, subtype: isTp ? f.subtype || null : null, telepass_id: f.telepass_id === NONE ? null : f.telepass_id, vehicle_id: f.vehicle_id === NONE ? null : f.vehicle_id };
      if (item.id) await api.put(`/requests/${item.id}`, payload); else await api.post("/requests", payload);
      toast.success(item.id ? "Richiesta aggiornata" : "Richiesta inviata");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? "Modifica richiesta" : "Nuova richiesta"}</DialogTitle>
          <DialogDescription>La richiesta viene inviata all'amministrazione per la tua stazione.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Richiesta">
            <Select value={f.type} onValueChange={(v) => setF((p) => ({ ...p, type: v, subtype: "" }))}>
              <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MATERIALE">MATERIALE</SelectItem><SelectItem value="TELEPASS">TELEPASS</SelectItem></SelectContent>
            </Select>
          </F>
          {isAdmin && !item.id ? <F label="Stazione">
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, telepass_id: NONE, vehicle_id: NONE }))}>
              <SelectTrigger data-testid="request-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F> : isTp ? null : <F label="Quantità"><Input value={f.quantity} onChange={set("quantity")} placeholder="Es. 10 pz" data-testid="request-quantity-input" /></F>}
          {isTp && <F label="Tipo richiesta Telepass" span={!isAdmin || !!item.id}>
            <Select value={f.subtype || undefined} onValueChange={set("subtype")}>
              <SelectTrigger data-testid="request-subtype-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{Object.entries(SUBTYPES).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`request-subtype-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          {!isTp && isAdmin && !item.id && <F label="Quantità" span><Input value={f.quantity} onChange={set("quantity")} placeholder="Es. 10 pz" data-testid="request-quantity-input" /></F>}
          {needTp && <F label="Telepass interessato" span>
            <PlateCombobox value={f.telepass_id} onChange={set("telepass_id")} placeholder="Seleziona Telepass" testId="request-telepass-select"
              options={tpOpts.map((t) => ({ value: t.id, label: `${t.number}${t.internal_number ? ` (int. ${t.internal_number})` : ""}${t.plate ? ` · ${t.plate}` : ""}` }))} />
          </F>}
          {isTp && f.subtype && <F label={f.subtype === "CAMBIO_TARGA" ? "Nuova targa" : "Furgone (facoltativo)"} span>
            <PlateCombobox value={f.vehicle_id} onChange={set("vehicle_id")} testId="request-vehicle-select"
              options={[...(f.subtype === "CAMBIO_TARGA" ? [] : [{ value: NONE, label: "— Nessun furgone" }]), ...vOpts.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}` }))]} />
          </F>}
          <F label={isTp ? "Descrizione" : "Descrizione: cosa serve"} span><Textarea rows={3} value={f.description} onChange={set("description")} placeholder={isTp ? "Dettagli della richiesta" : "Es. guanti, gilet alta visibilità, carrelli..."} data-testid="request-description-input" /></F>
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="request-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="request-save-button">{saving ? "Invio..." : item.id ? "Salva" : "Invia richiesta"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
